import { Alert, Button, Container } from 'react-bootstrap';
import { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAt, faHeadset, faPhoneAlt } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { AppShell } from '../../components/AppShell/AppShell';
import { Footer } from '../../components/Footer/Footer';
import { useFirebase } from '../../../contexts/FirebaseContext';
import { TicketForm } from '../../components/TicketForm/TicketForm';
import { Faq } from '../../components/Faq/Faq';
import { FullscreenLoader } from '../../components/FullscreenLoader/FullscreenLoader';
import { useMobileDetect } from '../../../hooks/useMobileDetect';
import { useCapacitor } from '../../../hooks/useCapacitor';
import { Title } from '../../components/Title/Title';
import { useGeolocation } from '../../../contexts/GeolocationContext';
import { isItalian } from '../../utils/utils';
import { TicketList } from '../../components/TicketList/TicketList';
import { PageHeader } from '../../components/PageHeader/PageHeader';
import styles from './Help.module.scss';

type HelpTab = 'ticket' | 'history' | 'contact' | 'faq';

const HelpComponent = () => {
  const { currentUser, staticData, getStaticData } = useFirebase();
  const history = useHistory();
  const location = useLocation();
  const { isMobile } = useMobileDetect();
  const { isNative, platform } = useCapacitor();
  const { t } = useTranslation();
  const { country_code } = useGeolocation();
  const [activeTab, setActiveTab] = useState<HelpTab>(() => new URLSearchParams(location.search).get('tab') === 'history' ? 'history' : 'faq');

  useEffect(() => {
    if (!Object.keys(staticData).length) getStaticData();
  }, []);

  const tabs: [HelpTab, string][] = [
    ['faq', t('help.labels.domandeFrequenti')],
    ['ticket', t('help.labels.apriTicket')],
    ['history', t('help.labels.storicoTicket')],
    ['contact', t('help.labels.altriCanali')],
  ];

  return (
    <AppShell>
      <div className={styles.page}>
        <Title>{t('help.labels.titolo')}</Title>

        <Container fluid className={styles.content} data-testid="help-page">
        <PageHeader
          title={t('help.labels.titoloAssistenza')}
          subtitle={t('help.labels.aiutoDescrizione')}
        />

        <section className={`tabbed-workspace ${styles.workspace}`} aria-label={t('help.labels.titolo')}>
          <div className="tabbed-workspace-tabs" role="tablist" aria-label={t('help.labels.titolo')}>
            {tabs.map(([tab, label]) => (
              <button
                key={tab}
                type="button"
                role="tab"
                id={`help-tab-${tab}`}
                aria-selected={activeTab === tab}
                aria-controls={`help-panel-${tab}`}
                className={activeTab === tab ? 'tabbed-workspace-active' : undefined}
                onClick={() => setActiveTab(tab)}
              >
                {label}
              </button>
            ))}
          </div>

          <div className={`tabbed-workspace-panel ${styles.panel}`}>
            {activeTab === 'ticket' && (
              <div id="help-panel-ticket" role="tabpanel" aria-labelledby="help-tab-ticket" className={styles.tabPanel}>
                <h2 className="sr-only">{t('help.labels.apriTicket')}</h2>
                {!currentUser && <Alert variant="warning">{t('help.labels.aiutoDescrizione4')}</Alert>}
                {!!currentUser && (
                    <TicketForm
                      wide
                       className="form-page form-page--xl"
                    submitLabel={t('help.labels.inviaTicket')}
                    onSuccess={(ticketId) => history.push(`/ticket/${ticketId}`)}
                  />
                )}
              </div>
            )}

            {activeTab === 'history' && (
              <div id="help-panel-history" role="tabpanel" aria-labelledby="help-tab-history" className={styles.tabPanel}>
                {!!currentUser ? <TicketList /> : <Alert variant="warning">{t('help.labels.aiutoDescrizione4')}</Alert>}
              </div>
            )}

            {activeTab === 'contact' && (
              <div id="help-panel-contact" role="tabpanel" aria-labelledby="help-tab-contact" className={`${styles.tabPanel} ${styles.contactPanel}`}>
                {staticData.ENABLE_MESSENGER && (
                  <div className={styles.contactSection}>
                    <h2 className={`text-page-subtitle ${styles.contactTitle}`}>{t('help.labels.scriviSuMessanger')}</h2>
                    <p className={styles.contactDescription}>{t('help.labels.messengerDescrizione')}</p>
                    <div className={styles.contactActions}>
                      <Button
                        className="tootor-button tootor-button--basic"
                        as="a"
                        href="https://m.me/433524160496487"
                        target="_blank"
                        rel="noreferrer"
                      >
                        {t('help.labels.scriviSuMessanger')}
                      </Button>
                    </div>
                  </div>
                )}

                {!!(staticData.SUPPORT_EMAIL || (staticData.SUPPORT_PHONE && isItalian(country_code))) && (
                  <div className={styles.contactSection}>
                    <h2 className={`text-page-subtitle ${styles.contactTitle}`}>{t('help.labels.altriCanali')}</h2>
                    <p className={styles.contactDescription}>{t('help.labels.scegliCanale')}</p>
                    {!!staticData.SUPPORT_EMAIL && (
                      <div className={styles.contactRow}>
                        <span>{t('help.labels.puoiScriverciA')}</span>
                        <a href={`mailto:${staticData.SUPPORT_EMAIL}`}>{staticData.SUPPORT_EMAIL}</a>
                      </div>
                    )}
                    {!!staticData.SUPPORT_PHONE && isItalian(country_code) && (
                      <div className={styles.contactRow}>
                        <span>{t('help.labels.puoiTelefonareA')}</span>
                        <span>{staticData.SUPPORT_PHONE}</span>
                        {!!staticData.SUPPORT_HOURS && <small>{t('help.labels.numeroAttivoAlle', { hours: staticData.SUPPORT_HOURS })}</small>}
                      </div>
                    )}
                  </div>
                )}

                {!isMobile && !!staticData.REMOTE_SUPPORT_DOWNLOAD_URL && (
                  <RemoteSupport
                    title={t('help.labels.assistenzaRemoto')}
                    copy={t('help.labels.necessitiSupporto')}
                    downloadCopy={t('help.labels.clickPerDownload')}
                    label={t('help.labels.scaricaSoftware', { name: staticData.REMOTE_SUPPORT_SOFTWARE_NAME || 'il software' })}
                    url={staticData.REMOTE_SUPPORT_DOWNLOAD_URL}
                  />
                )}

                {isNative && platform === 'ios' && !!staticData.REMOTE_SUPPORT_DOWNLOAD_URL_IOS && (
                  <RemoteSupport
                    title={t('help.labels.assistenzaRemoto')}
                    copy={t('help.labels.necessitiSupporto')}
                    downloadCopy={t('help.labels.clickPerDownload')}
                    label={t('help.labels.scaricaSoftware', { name: staticData.REMOTE_SUPPORT_SOFTWARE_NAME_IOS || t('help.labels.softwareGenerico') })}
                    url={staticData.REMOTE_SUPPORT_DOWNLOAD_URL_IOS}
                  />
                )}

                {isNative && platform === 'android' && !!staticData.REMOTE_SUPPORT_DOWNLOAD_URL_ANDROID && (
                  <RemoteSupport
                    title={t('help.labels.assistenzaRemoto')}
                    copy={t('help.labels.necessitiSupporto')}
                    downloadCopy={t('help.labels.clickPerDownload')}
                    label={t('help.labels.scaricaSoftware', { name: staticData.REMOTE_SUPPORT_SOFTWARE_NAME_ANDROID || t('help.labels.softwareGenerico') })}
                    url={staticData.REMOTE_SUPPORT_DOWNLOAD_URL_ANDROID}
                  />
                )}

              </div>
            )}

            {activeTab === 'faq' && (
              <div id="help-panel-faq" role="tabpanel" aria-labelledby="help-tab-faq" className={styles.tabPanel}>
                <h2>{t('help.labels.domandeFrequenti')}</h2>
                <Faq />
              </div>
            )}
          </div>
        </section>

        {!Object.keys(staticData).length && <FullscreenLoader fullscreen={false} />}
        </Container>

        <div className={styles.footer}>
          <Footer />
        </div>
      </div>
    </AppShell>
  );
};

const RemoteSupport = ({ title, copy, downloadCopy, label, url }: { title: string; copy: string; downloadCopy: string; label: string; url: string }) => (
  <div className={styles.contactSection}>
    <h2 className={`text-page-subtitle ${styles.contactTitle}`}>{title}</h2>
    <p className={styles.contactDescription}>
      {copy}
      <br />
      {downloadCopy}
    </p>
    <div className={styles.contactActions}>
      <Button variant="info" className="tootor-button tootor-button--basic" as="a" href={url} target="_blank">
        <span>{label}</span>
      </Button>
    </div>
  </div>
);

export default HelpComponent;
